import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companySettingsApi } from '../services/companySettingsApi';
import { authApiService } from '../../../shared/auth/services/authApi';
import type {
  CompanyAccountSettings,
  CompanyInfo,
  CompanyNotificationSettings,
  Device,
  EditMode,
} from '../types';

interface UseCompanyAccountSettingsReturn {
  // State
  settings: CompanyAccountSettings | null;
  devices: Device[];
  loading: boolean;
  error: string | null;
  editMode: EditMode;

  // Actions
  fetchSettings: () => Promise<void>;
  updateCompanyInfo: (data: CompanyInfo) => Promise<void>;
  updateEmail: (newEmail: string, password: string) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateNotifications: (notifications: CompanyNotificationSettings) => Promise<void>;
  terminateDevice: (deviceId: string) => Promise<void>;
  terminateAllSessions: () => Promise<void>;
  deleteAccount: (password: string, reason?: string) => Promise<void>;

  // UI State
  setEditMode: (mode: EditMode) => void;
  clearError: () => void;
}

export const useCompanyAccountSettings = (): UseCompanyAccountSettingsReturn => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<CompanyAccountSettings | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<EditMode>(null);

  /**
   * Helper function to logout and redirect to login page
   * Used after email or password change for security
   */
  const logoutAndRedirect = useCallback(async () => {
    try {
      await authApiService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/auth/login', {
      state: { message: 'Your credentials have been updated. Please log in again.' }
    });
  }, [navigate]);

  /**
   * Fetch account settings on mount
   */
  useEffect(() => {
    fetchSettings();
  }, []);

  /**
   * Fetch all account settings and devices
   */
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsData, devicesData] = await Promise.all([
        companySettingsApi.getAccountSettings(),
        companySettingsApi.getDevices(),
      ]);
      setSettings(settingsData);
      setDevices(devicesData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch settings';
      setError(errorMsg);
      console.error('Fetch settings error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update company information
   */
  const updateCompanyInfo = useCallback(
    async (data: CompanyInfo) => {
      setLoading(true);
      setError(null);
      try {
        await companySettingsApi.updateCompanyInfo(data);

        // Update local state
        if (settings) {
          setSettings({
            ...settings,
            companyInfo: data,
          });
        }

        setEditMode(null);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Update failed';
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [settings]
  );

  /**
   * Update email address
   * Uses Signup-Service for Supabase integration
   * Logs out user after successful update (requires re-login with new email)
   */
  const updateEmail = useCallback(
    async (newEmail: string, password: string) => {
      setLoading(true);
      setError(null);
      try {
        await companySettingsApi.updateEmail({ newEmail, password });

        // Email changed successfully - logout and redirect to login
        // User needs to re-login with the new email
        setEditMode(null);
        await logoutAndRedirect();
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Update failed';
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [logoutAndRedirect]
  );

  /**
   * Update password
   * Uses Signup-Service for Supabase integration
   * Logs out user after successful update (requires re-login with new password)
   */
  const updatePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      setLoading(true);
      setError(null);
      try {
        await companySettingsApi.updatePassword({ currentPassword, newPassword });

        // Password changed successfully - logout and redirect to login
        // User needs to re-login with the new password
        setEditMode(null);
        await logoutAndRedirect();
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Update failed';
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [logoutAndRedirect]
  );

  /**
   * Update notification settings
   */
  const updateNotifications = useCallback(
    async (notifications: CompanyNotificationSettings) => {
      setLoading(true);
      setError(null);
      try {
        await companySettingsApi.updateNotificationSettings(notifications);

        // Update local state
        if (settings) {
          setSettings({
            ...settings,
            notificationSettings: notifications,
          });
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Update failed';
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [settings]
  );

  /**
   * Terminate a specific device session
   */
  const terminateDevice = useCallback(
    async (deviceId: string) => {
      setLoading(true);
      setError(null);
      try {
        await companySettingsApi.terminateDevice({ deviceId });

        // Remove device from local state
        setDevices((prev) => prev.filter((device) => device.id !== deviceId));
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Termination failed';
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Terminate all other device sessions
   */
  const terminateAllSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await companySettingsApi.terminateAllOtherSessions();

      // Keep only current device
      setDevices((prev) => prev.filter((device) => device.isCurrent));
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Termination failed';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete account
   */
  const deleteAccount = useCallback(
    async (password: string, reason?: string) => {
      setLoading(true);
      setError(null);
      try {
        await companySettingsApi.deleteAccount({ password, reason });

        // Clear local storage and redirect will be handled by component
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Delete failed';
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    settings,
    devices,
    loading,
    error,
    editMode,
    fetchSettings,
    updateCompanyInfo,
    updateEmail,
    updatePassword,
    updateNotifications,
    terminateDevice,
    terminateAllSessions,
    deleteAccount,
    setEditMode,
    clearError,
  };
};
